import { GripVertical } from "lucide-react";
import { useCallback, useRef } from "react";
import { FormProvider } from "react-hook-form";

import { Switch } from "@/components/switch";
import { Header } from "@/features/request/edit/components/header";
import { HeaderSkeleton } from "@/features/request/edit/components/header/HeaderSkeleton";
import {
  CollapsibleSectionsProvider,
  useCollapsibleSections,
} from "@/features/request/edit/contexts/collapsible-sections.context";
import { EditRequestModalsProvider } from "@/features/request/edit/contexts/edit-request-modals.context";
import { useInitializeRequestForm } from "@/features/request/edit/hooks/useInitializeRequestForm";
import { usePersistedChatWidth } from "@/features/request/edit/hooks/usePersistedChatWidth";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

import { StickyWidget } from "../../../shared/components/sticky-widget";
import { AssetsSection } from "./assets-section";
import { BrandSection } from "./brand-section";
import { ChatSection } from "./chat-section";
import { CollaboratorsSection } from "./collaborators-section";
import { DesignBriefSection } from "./design-brief-section";
import { InspirationSection } from "./inspirations-section";
import { RequestDescriptionSection } from "./request-description-section";
import { RequestDetailsSection } from "./request-details-section";
import { RequestFormSkeleton } from "./RequestFormSkeleton";
import { TechnicalDetailsSection } from "./technical-details-section";
import { TextForDesignSection } from "./text-for-design-section";

function ExpandAllToggle() {
  const { expandAll, isAllExpanded } = useCollapsibleSections();

  return (
    <div className="py-md">
      <Switch isSelected={isAllExpanded} onChange={expandAll}>
        Expand all
      </Switch>
    </div>
  );
}

interface RequestFormLayoutProps {
  banner?: React.ReactNode;
  openInternalNotes?: boolean;
}

const CHAT_DEFAULT = 45;
const MIN_PANEL_PX = 300;
const LARGE_SCREEN_PX = 1440;
const LARGE_SCREEN_MIN_PANEL_PX = 480;

export function EditRequestLayout({
  banner,
  openInternalNotes,
}: RequestFormLayoutProps) {
  const { formMethods } = useInitializeRequestForm();
  const { data: request } = useGetRequest();
  const [chatWidth, setChatWidth] = usePersistedChatWidth(CHAT_DEFAULT);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const handle = e.currentTarget;

    handle.setPointerCapture(e.pointerId);
    isDragging.current = true;

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const offsetFromRight = rect.right - moveEvent.clientX;
      const pct = (offsetFromRight / rect.width) * 100;
      const minPanelPx =
        window.innerWidth >= LARGE_SCREEN_PX
          ? LARGE_SCREEN_MIN_PANEL_PX
          : MIN_PANEL_PX;
      const minPct = Math.min(50, (minPanelPx / rect.width) * 100);

      setChatWidth(Math.min(100 - minPct, Math.max(minPct, pct)));
    };

    const stopDragging = () => {
      isDragging.current = false;
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", stopDragging);
      handle.removeEventListener("pointercancel", stopDragging);
    };

    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", stopDragging);
    handle.addEventListener("pointercancel", stopDragging);
  }, []);

  return (
    <FormProvider {...formMethods}>
      {banner}
      <StickyWidget
        openInternalNotes={openInternalNotes}
        projectId={request?.projectId}
        requestId={request?.id}
      />
      <EditRequestModalsProvider>
        <CollapsibleSectionsProvider>
          <form className="bg-bg-primary flex flex-col flex-grow-1 w-full sm:h-full sm:overflow-y-hidden sm:rounded-xl">
            {request ? <Header /> : <HeaderSkeleton />}
            <div
              className="flex sm:flex-1 px-xl sm:flex-row flex-col-reverse sm:min-h-0 gap-xl sm:gap-0"
              ref={containerRef}
            >
              <section
                className="relative flex flex-col gap-md sm:overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] sm:min-h-0 sm:touch-pan-y sm:overscroll-contain w-full sm:w-[var(--content-width)]"
                style={
                  {
                    "--content-width": `${100 - chatWidth}%`,
                  } as React.CSSProperties
                }
              >
                {request ? (
                  <>
                    <ExpandAllToggle />
                    <div className="flex flex-col gap-xl">
                      <RequestDetailsSection />
                      <RequestDescriptionSection />
                      <TextForDesignSection />
                      <DesignBriefSection />
                      <TechnicalDetailsSection />
                      <AssetsSection />
                      <InspirationSection />
                      <BrandSection />
                      <CollaboratorsSection />
                    </div>
                  </>
                ) : (
                  <RequestFormSkeleton />
                )}
              </section>

              <div
                className="hidden sm:flex items-center justify-center w-3 cursor-col-resize shrink-0 group touch-none"
                onPointerDown={onPointerDown}
              >
                <div className="h-full w-px bg-border-primary group-hover:bg-border-brand transition-colors" />
                <GripVertical
                  className="absolute text-fg-tertiary group-hover:text-fg-secondary transition-colors"
                  size={16}
                />
              </div>

              <section
                className="flex items-start justify-center pb-xl pt-md pl-2 h-[55dvh] sm:h-auto w-full sm:w-[var(--chat-width)]"
                style={
                  { "--chat-width": `${chatWidth}%` } as React.CSSProperties
                }
              >
                <div className="w-full h-full rounded-xl bg-bg-secondary p-3xl pt-md overflow-hidden">
                  <ChatSection />
                </div>
              </section>
            </div>
          </form>
        </CollapsibleSectionsProvider>
      </EditRequestModalsProvider>
    </FormProvider>
  );
}
